import {
  listingCacheDefaults,
  type ListingCacheOptions,
} from '@vs-shop/engine/listing/atom/makeListingAtoms';
import { productsPerPageDefault } from '@vs-shop/engine/config/pagination';
import type { ListingScope } from '@vs-shop/engine/capability/productListing/listingScope';
import { querySyntax } from '@vs-shop/engine/listing/url/querySyntax';
import type { ListingUrlSyntax } from '@vs-shop/engine/listing/url/ListingUrlSyntax';
import type { QueryPair } from '@vs-shop/engine/listing/url/listingUrl';
import { SortToken } from '@vs-shop/engine/listing/url/filterTokens';
import { ListingVocabulary } from '@vs-shop/engine/listing/vocabulary/ListingVocabulary';

export const RCPROFI_LISTING_ITEMS_PER_PAGE = productsPerPageDefault;

export const RCPROFI_LISTING_FACET_LAYOUT: 'fixed' | 'dynamic' = 'fixed';

const listingOrders: ReadonlyArray<{
  readonly backend: string;
  readonly neutral: string;
}> = [
  { backend: 'PRICE_ASC', neutral: 'price-asc' },
  { backend: 'PRICE_DESC', neutral: 'price-desc' },
  { backend: 'CREATED_AT_ASC', neutral: 'newest' },
];

const query = querySyntax({
  filterPrefix: 'f_',
  rangeMinSuffix: '_min',
  rangeMaxSuffix: '_max',
  valueSeparator: '_',
  pageParam: 'page',
  sortParam: 'order',
  preserve: ['q', 'taxon'],
});

const toNeutralOrder = (pair: QueryPair): QueryPair => {
  if (pair[0] !== 'order') return pair;
  const token = listingOrders.find((order) => order.backend === pair[1])?.neutral;
  return ['order', token ?? pair[1]];
};

const toBackendOrder = (pair: QueryPair): QueryPair => {
  if (pair[0] !== 'order') return pair;
  const value = listingOrders.find((order) => order.neutral === pair[1])?.backend;
  return ['order', value ?? pair[1]];
};

/** RCProfi keeps its existing uppercase order URL values. */
export const RCPROFI_LISTING_SYNTAX: ListingUrlSyntax = {
  ...query,
  read: (url, hints) => query.read({ ...url, query: url.query.map(toNeutralOrder) }, hints),
  write: (base, tokens, preserved) => {
    const written = query.write(base, tokens, preserved);
    return { ...written, query: written.query.map(toBackendOrder) };
  },
};

export const splitRcprofiListingQuery = (
  _scope: ListingScope,
  queryPairs: ReadonlyArray<QueryPair>
): {
  readonly listing: ReadonlyArray<QueryPair>;
  readonly passthrough: ReadonlyArray<QueryPair>;
} => {
  const isListing = ([name]: QueryPair) =>
    RCPROFI_LISTING_SYNTAX.reservedNames.includes(name) || name.startsWith('f_');
  return {
    listing: queryPairs.filter(isListing),
    passthrough: queryPairs.filter((pair) => !isListing(pair)),
  };
};

/** Scope vocabularies add facet entries to these established RCProfi sort tokens. */
export const rcprofiListingVocabulary = ListingVocabulary.make({
  entries: [],
  sorts: [
    {
      token: SortToken.make('price-asc'),
      sort: { field: 'price', direction: 'asc' },
    },
    {
      token: SortToken.make('price-desc'),
      sort: { field: 'price', direction: 'desc' },
    },
    {
      token: SortToken.make('newest'),
      sort: { field: 'created', direction: 'desc' },
    },
  ],
});

/** Controls listing memory and speculative reads for this tenant. */
export const RCPROFI_LISTING_CACHE: ListingCacheOptions = { ...listingCacheDefaults };
